import React, { useState } from "react";
import { toast } from "react-toastify";
import { useParams, useSearchParams } from "react-router-dom";
import { Button, Typography, Box } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import FormDrawer from "components/Shared/FormDrawer";
import AppTable from "components/Shared/AppTable";
import { BaseTabs } from "components/Shared/BaseTabs";
import DriverHistoryForm from "./DriverHistoryForm";
import {
  useAddDriverHistoryMutation,
  useUpdateDriverHistoryMutation,
  useLazyListDriverHistoryQuery,
} from "store/services/driverApi";
import { useAuth } from "hooks";
import { useTablePagination } from "hooks";

const DriverHistoryTabs = () => {
  const { id } = useParams();
  const { loggedInUser, isDriver } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const driverId = isDriver ? loggedInUser.typeRef : id;

  const historyTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [fetchHistories, { data, isLoading, isFetching, error }] =
    useLazyListDriverHistoryQuery();

  const [addHistory] = useAddDriverHistoryMutation();
  const [updateHistory] = useUpdateDriverHistoryMutation();

  React.useEffect(() => {
    fetchHistories({
      driverId,
      page: currentPage,
      limit: perPage,
      active: activeTab === "Active",
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchHistories, keyword, perPage, driverId]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedHistory) {
        await updateHistory({
          driverId,
          tripId: selectedHistory._id,
          ...formData,
        }).unwrap();
        toast.success("Driver history updated successfully");
      } else {
        await addHistory({ driverId, ...formData }).unwrap();
        toast.success("Driver history added successfully");
      }
      setShowForm(false);
      setSelectedHistory(null);
    } catch (err) {
      toast.error("An error occurred");
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name || "N/A",
      sortable: true,
    },
    {
      name: "Trip ID",
      selector: (row) => row.tripIdd || "N/A",
      cell: (row) => row.tripIdd || "N/A",
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      cell: (row) => row.description || "N/A",
      sortable: true,
    },
    {
      name: "Date From",
      selector: (row) => row.dateFrom,
      cell: (row) => (
        <span>
          {row.dateFrom ? new Date(row.dateFrom).toLocaleDateString() : "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Date To",
      selector: (row) => row.dateTo,
      cell: (row) => (
        <span>
          {row.dateTo ? new Date(row.dateTo).toLocaleDateString() : "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          onClick={() => {
            setSelectedHistory(row);
            setShowForm(true);
          }}
        >
          <Edit fontSize="small" />
        </Button>
      ),
    },
  ];

  const breadcrumbs = [{ title: "Driver History" }];

  return (
    <>
      {isDriver ? (
        <BreadcrumbLayout
          breadcrumbs={breadcrumbs}
          headerActions={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedHistory(null);
                setShowForm(true);
              }}
            >
              Add History
            </Button>
          }
        >
          <BreadcrumbLayout.Error error={error} />
          <BreadcrumbLayout.Paper>
            <BaseTabs tabs={historyTabs} defaultValue={activeTab} />
            <AppTable
              Directive
              columns={columns}
              data={data?.driverHistories || []}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={data?.totalItems || 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              noDataComponent={
                <Typography py={3} textAlign="center" color="textSecondary">
                  No histories found
                </Typography>
              }
            />
          </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedHistory(null);
                setShowForm(true);
              }}
            >
              Add History
            </Button>
          </Box>
          <BaseTabs tabs={historyTabs} defaultValue={activeTab} />
          <AppTable
            columns={columns}
            data={data?.driverHistories || []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={data?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Typography py={3} textAlign="center" color="textSecondary">
                No histories found
              </Typography>
            }
          />
        </Box>
      )}

      <FormDrawer
        open={showForm}
        onClose={() => setShowForm(false)}
        title={`${selectedHistory ? "Edit" : "Add"} Driver History`}
      >
        <DriverHistoryForm
          driverHistory={selectedHistory}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isSubmitting={false}
        />
      </FormDrawer>
    </>
  );
};

export default DriverHistoryTabs;
